import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { BOOK } from "../content";
import { EyeMark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCard = (v) =>
  v.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();

const formatExpiry = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });

const Checkout = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({ provider: "mock" });
  const [email, setEmail] = useState("");
  const [card, setCard] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    axios
      .get(`${API}/payments/config`)
      .then((r) => setConfig(r.data))
      .catch(() => {});
  }, []);

  const isRazorpay = config.provider === "razorpay";

  const validate = () => {
    const e = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Enter a valid email — your book is delivered here.";
    if (!isRazorpay) {
      if (card.replace(/\s/g, "").length !== 16) e.card = "Card number must be 16 digits.";
      if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry)) e.expiry = "Use MM/YY.";
      if (!/^\d{3,4}$/.test(cvc)) e.cvc = "3–4 digits.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const payWithRazorpay = async (order) => {
    await loadRazorpayScript();
    const { data: rp } = await axios.post(`${API}/orders/${order.order_id}/razorpay`);
    const rzp = new window.Razorpay({
      key: rp.key_id,
      amount: rp.amount,
      currency: rp.currency,
      order_id: rp.razorpay_order_id,
      name: "The End of the Obvious",
      description: "Reader's Edition — PDF download",
      prefill: { email },
      theme: { color: "#D4AF37" },
      modal: { ondismiss: () => setProcessing(false) },
      handler: async (res) => {
        try {
          await axios.post(`${API}/orders/${order.order_id}/razorpay/verify`, {
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          navigate(`/success?order=${order.order_id}`);
        } catch {
          setProcessing(false);
          toast.error("Payment verification failed. If you were charged, contact us.");
        }
      },
    });
    rzp.open();
  };

  const pay = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setProcessing(true);
    try {
      const { data: order } = await axios.post(`${API}/orders`, {
        email,
        origin_url: window.location.origin,
      });
      if (isRazorpay) {
        await payWithRazorpay(order);
        return;
      }
      await axios.post(`${API}/orders/${order.order_id}/pay`);
      navigate(`/success?order=${order.order_id}`);
    } catch (err) {
      setProcessing(false);
      toast.error("Payment could not be completed. Please try again.");
    }
  };

  const inputCls = (bad) =>
    `w-full rounded-lg border bg-[#0A404E] px-4 py-3.5 text-[#F8FAFC] placeholder:text-[#94A3B8]/50 outline-none transition-colors duration-300 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] ${
      bad ? "border-red-500/60" : "border-[#D4AF37]/20"
    }`;

  return (
    <div className="min-h-screen grain bg-[#052730] pt-10 pb-20" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#FCD34D] transition-colors" data-testid="checkout-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to the Archive
          </Link>
          <EyeMark className="w-8 h-8" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 grid lg:grid-cols-5 gap-8"
        >
          <form onSubmit={pay} className="lg:col-span-3 space-y-6" data-testid="checkout-form" noValidate>
            <div className="rounded-lg border border-[#D4AF37]/20 bg-[#06333E] p-6 sm:p-8">
              <h2 className="font-display text-lg font-bold tracking-widest text-[#F8FAFC]">DELIVERY EMAIL</h2>
              <p className="mt-1 text-xs text-[#94A3B8]">Your download link lands here the second you pay.</p>
              <input
                data-testid="checkout-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={`mt-4 ${inputCls(errors.email)}`}
                autoComplete="email"
              />
              {errors.email && <p className="mt-2 text-xs text-red-400" data-testid="email-error">{errors.email}</p>}
            </div>

            {isRazorpay ? (
              <div className="rounded-lg border border-[#D4AF37]/20 bg-[#06333E] p-6 sm:p-8" data-testid="razorpay-info">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-lg font-bold tracking-widest text-[#F8FAFC]">PAYMENT</h2>
                  <Lock className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">
                  You'll complete your ${BOOK.price.toFixed(2)} payment in Razorpay's secure window — cards, UPI, and netbanking accepted.
                </p>
                <p className="mt-4 flex items-center gap-2 text-[11px] text-[#94A3B8]">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" /> PCI-DSS compliant. Your card never touches our servers.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-[#D4AF37]/20 bg-[#06333E] p-6 sm:p-8">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-lg font-bold tracking-widest text-[#F8FAFC]">PAYMENT</h2>
                  <Lock className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div className="mt-4 space-y-4">
                  <div>
                    <input
                      data-testid="checkout-card-input"
                      inputMode="numeric"
                      value={card}
                      onChange={(e) => setCard(formatCard(e.target.value))}
                      placeholder="4242 4242 4242 4242"
                      className={inputCls(errors.card)}
                      autoComplete="cc-number"
                    />
                    {errors.card && <p className="mt-2 text-xs text-red-400" data-testid="card-error">{errors.card}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <input
                        data-testid="checkout-expiry-input"
                        inputMode="numeric"
                        value={expiry}
                        onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                        placeholder="MM/YY"
                        className={inputCls(errors.expiry)}
                        autoComplete="cc-exp"
                      />
                      {errors.expiry && <p className="mt-2 text-xs text-red-400" data-testid="expiry-error">{errors.expiry}</p>}
                    </div>
                    <div>
                      <input
                        data-testid="checkout-cvc-input"
                        inputMode="numeric"
                        value={cvc}
                        onChange={(e) => setCvc(e.target.value.replace(/\D/g, "").slice(0, 4))}
                        placeholder="CVC"
                        className={inputCls(errors.cvc)}
                        autoComplete="cc-csc"
                      />
                      {errors.cvc && <p className="mt-2 text-xs text-red-400" data-testid="cvc-error">{errors.cvc}</p>}
                    </div>
                  </div>
                </div>
                <p className="mt-4 flex items-center gap-2 text-[11px] text-[#94A3B8]">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" /> 256-bit encrypted. Your card never touches our servers.
                </p>
              </div>
            )}

            <button
              data-testid="checkout-pay-button"
              type="submit"
              disabled={processing}
              className="w-full rounded-full bg-[#D4AF37] py-5 text-lg font-semibold text-[#052730] transition-all duration-300 hover:bg-[#FCD34D] hover:shadow-[0_0_40px_rgba(212,175,55,0.45)] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#FCD34D] active:scale-[0.98] flex items-center justify-center gap-3"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Processing payment…
                </>
              ) : isRazorpay ? (
                <>Pay ${BOOK.price.toFixed(2)} via Razorpay</>
              ) : (
                <>Pay ${BOOK.price.toFixed(2)} & Download Now</>
              )}
            </button>
          </form>

          <motion.aside
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-2"
          >
            <div className="rounded-lg border border-[#D4AF37]/20 bg-[#06333E] p-6 sm:p-8 lg:sticky lg:top-10" data-testid="order-summary">
              <h2 className="font-display text-lg font-bold tracking-widest text-[#F8FAFC]">ORDER SUMMARY</h2>
              <div className="mt-6 flex gap-4">
                <div className="w-16 shrink-0 aspect-[3/4] rounded bg-gradient-to-br from-[#084049] to-[#052730] border border-[#D4AF37]/30 flex items-center justify-center">
                  <EyeMark className="w-7 h-7" />
                </div>
                <div>
                  <p className="font-display font-semibold text-[#F8FAFC]">{BOOK.title}</p>
                  <p className="text-[11px] text-[#94A3B8] mt-0.5">Reader's Edition · PDF · {BOOK.pages} pages</p>
                </div>
              </div>
              <div className="mt-6 space-y-2 text-sm border-t border-[#D4AF37]/15 pt-4">
                <div className="flex justify-between text-[#94A3B8]">
                  <span>Original</span>
                  <span className="line-through">${BOOK.originalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#94A3B8]">
                  <span>Early-reader discount (67%)</span>
                  <span className="text-[#D4AF37]">−${(BOOK.originalPrice - BOOK.price).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl font-semibold text-[#F8FAFC] pt-2 border-t border-[#D4AF37]/15">
                  <span>Total</span>
                  <span data-testid="checkout-total">${BOOK.price.toFixed(2)}</span>
                </div>
              </div>
              <p className="mt-6 text-[11px] leading-relaxed text-[#94A3B8]">
                Digital delivery: instant download + email link. Digital goods are non-refundable once downloaded —{" "}
                <a href="/policies#refunds" className="text-[#FCD34D] underline underline-offset-2 hover:text-[#D4AF37]" data-testid="checkout-refund-policy-link">
                  see refund policy
                </a>
                .
              </p>
            </div>
          </motion.aside>
        </motion.div>
      </div>
    </div>
  );
};

export default Checkout;
